import moment from 'moment';
import { day, month, minute, hour, year, second } from '@/constants';

type ToFormattedDateOptions = {
  format: string;
};

type ToTimeAgoOptions = ToFormattedDateOptions & {
  justNowLabel: string;
  justNowTime: number;
  format: string;
};

const defaultTimeAgoOptions: Readonly<ToTimeAgoOptions> = {
  justNowLabel: 'Just Now!',
  justNowTime: 59 * second,
  format: 'MMMM Do',
};

const defaultFormattedDateOptions: Readonly<ToFormattedDateOptions> = {
  format: 'MMMM Do',
};

function simplePlural(value: number, word: string) {
  const plural = value !== 1 ? 's' : '';
  return `${word}${plural}`;
}

export function toJustTimeAgo(timestamp: number) {
  const elapsed = Date.now() - Math.floor(timestamp);

  if (elapsed < minute) {
    const seconds = Math.round(elapsed / 1000);
    return `${seconds < 0 ? 0 : seconds} ${simplePlural(seconds, 'second')} ago`;
  }

  if (elapsed < hour) {
    const minuteValue = Math.round(elapsed / minute);
    return `${minuteValue} ${simplePlural(minuteValue, 'minute')} ago`;
  }

  if (elapsed < day) {
    const hourValue = Math.round(elapsed / hour);
    return `${hourValue} ${simplePlural(hourValue, 'hour')} ago`;
  }

  if (elapsed < month) {
    const dayValue = Math.round(elapsed / day);
    return `${dayValue} ${simplePlural(dayValue, 'day')} ago`;
  }

  if (elapsed < year) {
    const monthValue = Math.round(elapsed / month);
    return `${monthValue} ${simplePlural(monthValue, 'month')} ago`;
  }

  const yearValue = Math.round(elapsed / year);
  return `${yearValue} ${simplePlural(yearValue, 'year')} ago`;
}

/**
 * Convert a timestamp to the requested format.
 *
 * This will return the string as is if it's invalid.
 *
 * @param {number} timestamp
 * @param {object} options
 */
export function toFormattedDate(timestamp: number, options: Partial<ToFormattedDateOptions> = {}) {
  const functionOptions = {
    ...defaultFormattedDateOptions,
    ...options,
  };

  const momentDate = moment(timestamp);
  if (!momentDate.isValid()) {
    return timestamp;
  }
  return momentDate.format(functionOptions.format);
}

/**
 * Convert a timestamp to an ISO date time string.
 */
export function toISODate(timestamp: number) {
  const momentDate = moment(timestamp);
  if (!momentDate.isValid()) {
    return timestamp;
  }
  return momentDate.toISOString();
}

/**
 * Convert a timestamp to a time-ago display.
 *
 * This will return the string as is if it's invalid. It will show time-ago for
 * dates > 10 seconds ago and <= 30 days ago, otherwise it will fall back to the
 * formatted date.
 */
export function toTimeAgo(timestamp: number | string, options: Partial<ToTimeAgoOptions> = {}) {
  const timestampType = typeof timestamp;
  const isStringNumber = typeof timestamp === 'string' && /^\d+$/.test(timestamp);
  if (timestampType === 'string' && !isStringNumber) {
    return timestamp;
  }

  if (typeof timestamp === 'string') {
    return toTimeAgo(Number(timestamp), options);
  }

  const functionOptions: ToTimeAgoOptions = {
    ...defaultTimeAgoOptions,
    ...options,
  };

  const now = Date.now();
  const timestampIsFromTheFuture = now < timestamp;

  // bail "just now" for recent things or timestamps from the future
  if (timestampIsFromTheFuture || now - timestamp <= functionOptions.justNowTime) {
    return `${functionOptions.justNowLabel}`;
  }

  const momentDate = moment(timestamp);
  if (!momentDate.isValid()) {
    return timestamp;
  }

  return toJustTimeAgo(timestamp);
}
